import apiClient, { unwrapData } from "./apiClient";
import { filterDashboardCalls, mapHistoryCallToDashboardCall } from "./liveDataAdapters";
import { PAGE_SIZE } from "../utils/constants";

const DASHBOARD_CALL_LIMIT = 1000;

function buildPerformanceFromAggregates(items = [], stats = {}) {
  const days = new Map();
  const today = new Date();

  for (let i = 6; i >= 0; i -= 1) {
    const day = new Date(today);
    day.setDate(day.getDate() - i);
    const key = day.toISOString().slice(0, 10);
    days.set(key, { label: String(day.getDate()), incoming: 0, answer: 0, shadow: 0 });
  }

  items.forEach((item) => {
    const key = String(item.call_date || "").slice(0, 10);

    if (days.has(key)) {
      const day = days.get(key);
      day.incoming += Number(item.totalCalls || 0);
      day.answer += Number(item.completedCalls || 0);
      day.shadow = Math.round((day.incoming + day.answer) / 2);
    }
  });

  const totalCalls = Number(stats.total_calls || 0);
  const totalBookings = Number(stats.total_bookings || 0);
  const avgDurationSeconds = Number(stats.avg_duration || 0);
  const answerRate = totalCalls > 0
    ? `${((totalBookings / totalCalls) * 100).toFixed(2)}%`
    : "0%";

  return {
    chartData: Array.from(days.values()),
    metrics: [
      {
        key: "talk-time",
        label: "AVG TALK TIME",
        value: avgDurationSeconds ? `${avgDurationSeconds}s` : "--",
      },
      { key: "wait-time", label: "AVG WAIT TIME", value: "--" },
      { key: "answered-calls", label: "ANSWERED CALLS", value: answerRate },
    ],
  };
}

async function loadDashboardCalls(options = {}) {
  const response = await apiClient.get("/api/calls", {
    ...options,
    query: {
      ...(options.query || {}),
      view: "canonical",
      limit: options.query?.limit || DASHBOARD_CALL_LIMIT,
      page: options.query?.page || 1,
    },
  });

  const items = unwrapData(response)?.items || [];
  return items.map(mapHistoryCallToDashboardCall);
}

export async function getDashboardStats(options = {}) {
  const { signal } = options;
  const perfOptions = signal ? { signal } : {};

  const [statsResponse, performanceResponse] = await Promise.all([
    apiClient.get("/api/stats", options),
    apiClient.get("/api/dashboard/call-performance", perfOptions),
  ]);

  const stats = unwrapData(statsResponse) || {};
  const performanceData = unwrapData(performanceResponse) || {};
  const totalCalls = Number(stats.total_calls || 0);
  const totalBookings = Number(stats.total_bookings || 0);
  const bookingRate = totalCalls > 0
    ? Number(((totalBookings / totalCalls) * 100).toFixed(2))
    : 0;

  return {
    ...stats,
    booking_rate: bookingRate,
    performance: buildPerformanceFromAggregates(
      Array.isArray(performanceData.items) ? performanceData.items : [],
      stats,
    ),
  };
}

export async function getDashboardCalls(params = {}, options = {}) {
  const allCalls = await loadDashboardCalls(options);
  const filteredCalls = filterDashboardCalls(allCalls, params);
  const currentPage = Number(params.offset || 0) / PAGE_SIZE;
  const startIndex = Number(params.offset || 0);
  const endIndex = startIndex + Number(params.limit || PAGE_SIZE);

  return {
    calls: filteredCalls.slice(startIndex, endIndex),
    total: filteredCalls.length,
    page: Number.isFinite(currentPage) ? currentPage + 1 : 1,
  };
}
